import { useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Calendar,
  CreditCard,
  User,
  UserCircle,
  Settings,
  CircleHelp,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Target,
  BookOpen,
  Activity,
  ShoppingBag,
} from "lucide-react";
import { cn, Button, toast, CircleImageWithSkeleton } from "@cliently/ui";
import { useUiStore } from "../../stores/ui.store";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { usePortalConversations } from "../../hooks/usePortalChat";
import { useOrganizations } from "../../hooks/use-organizations";
import { useClientProfile } from "../../hooks/use-client-profile";
import { ALL_CLIENTS_KEY, useOrgStore } from "../../stores/org.store";
import { resolveOrganizationMediaUrl } from "../../lib/organization-media-url";
import {
  portalProfessionTranslationKey,
  resolveNumericTrackingConfigured,
} from "@cliently/types";

const useCoachBranding = () => {
  return useQuery({
    queryKey: ["coach-branding"],
    queryFn: async () => {
      const { data } = await api.get("/portal/about");
      return data as {
        name: string;
        logo: string | null;
        profilePhoto: string | null;
        professionType: string | null;
        customProfession: string | null;
      } | null;
    },
    staleTime: Infinity,
    retry: false,
  });
};

export const ClientSidebar = () => {
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUiStore();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: org } = useCoachBranding();
  const { data: organizations = [] } = useOrganizations();
  const isOnChatList = location.pathname === "/chat";
  const { data: conversations = [] } = usePortalConversations({ isOnChatList });
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const getActiveOrg = useOrgStore((s) => s.getActiveOrg);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: profile } = useClientProfile();
  const activeMembership =
    organizations.find((o) => o.clientId === activeClientId) ?? getActiveOrg();
  /** Même source que /metrics et le tableau de bord : le profil courant, avec repli sur la liste des orgs. */
  const portalFeatures =
    (!isAllProsMode ? profile?.org?.clientPortalFeatures : undefined) ??
    activeMembership?.clientPortalFeatures ??
    null;
  const showMetricsNav = resolveNumericTrackingConfigured(portalFeatures);
  const coachImageUrl = resolveOrganizationMediaUrl(
    org?.profilePhoto ?? org?.logo,
  );
  const knownMessageIdsRef = useRef<Set<string>>(new Set());

  const unreadChatCount = useMemo(() => {
    if (location.pathname.startsWith("/chat")) return 0;
    return conversations.filter((conversation) => conversation.hasUnread)
      .length;
  }, [conversations, location.pathname]);

  useEffect(() => {
    if (!conversations.length) return;

    const knownMessageIds = knownMessageIdsRef.current;
    if (knownMessageIds.size === 0) {
      conversations.forEach((conversation) => {
        if (conversation.lastMessage?.id) {
          knownMessageIds.add(conversation.lastMessage.id);
        }
      });
      return;
    }

    if (location.pathname.startsWith("/chat")) {
      conversations.forEach((conversation) => {
        if (conversation.lastMessage?.id) {
          knownMessageIds.add(conversation.lastMessage.id);
        }
      });
      return;
    }

    conversations.forEach((conversation) => {
      const lastMessage = conversation.lastMessage;
      if (!lastMessage) return;
      if (knownMessageIds.has(lastMessage.id)) return;

      knownMessageIds.add(lastMessage.id);
      if (!conversation.hasUnread) return;

      toast.info(
        `Nouveau message de ${conversation.peerLabel ?? "votre praticien"}`,
        {
          description: lastMessage.content,
          action: {
            label: t("chat.openConversation", "Ouvrir"),
            onClick: () => {
              void navigate(`/chat/${conversation.id}`);
              if (window.innerWidth < 768) setSidebarOpen(false);
            },
          },
        },
      );
    });
  }, [conversations, location.pathname, navigate, setSidebarOpen, t]);

  const navItems = useMemo(() => {
    const orgCount = organizations.length || 1;
    type NavItem = {
      to: string;
      label: string;
      icon: typeof LayoutDashboard;
      end?: boolean;
    };
    const items: NavItem[] = [
      {
        to: "/dashboard",
        label: t("nav.dashboard", "Tableau de bord"),
        icon: LayoutDashboard,
        end: true,
      },
      {
        to: "/appointments",
        label: t("nav.appointments", "Rendez-vous"),
        icon: Calendar,
      },
      {
        to: "/programs",
        label: t("nav.programs", "Programmes"),
        icon: BookOpen,
      },
    ];
    if (showMetricsNav) {
      items.push({
        to: "/metrics",
        label: t("nav.metrics", "Métriques"),
        icon: Activity,
      });
    }
    items.push({
      to: "/objectives",
      label: t("nav.objectives", "Objectifs"),
      icon: Target,
    });
    items.push(
      {
        to: "/services",
        label: t("nav.services", "Services"),
        icon: ShoppingBag,
      },
      {
        to: "/chat",
        label: t("nav.chat", "Messages"),
        icon: MessageCircle,
      },
      {
        to: "/payments",
        label: t("nav.payments", "Paiements"),
        icon: CreditCard,
      },
      { to: "/profile", label: t("nav.profile", "Mon profil"), icon: User },
      {
        to: "/about",
        label: t("nav.about", {
          count: orgCount,
          defaultValue: orgCount > 1 ? "Mes experts" : "Mon expert",
        }),
        icon: UserCircle,
      },
      {
        to: "/settings",
        label: t("nav.settings", "Paramètres"),
        icon: Settings,
      },
    );
    return items;
  }, [organizations.length, showMetricsNav, t]);

  const helpItem = {
    to: "/requests",
    label: t("nav.requests", "Aide"),
    icon: CircleHelp,
  };

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          "bg-sidebar fixed inset-y-0 left-0 z-50 flex flex-col border-r transition-all duration-300",
          sidebarOpen ? "w-64" : "w-16",
          "max-md:translate-x-0",
          !sidebarOpen && "max-md:-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center justify-between border-b px-4">
          {sidebarOpen ? (
            <div className="flex items-center gap-2 overflow-hidden">
              {!isAllProsMode && coachImageUrl ? (
                <CircleImageWithSkeleton
                  src={coachImageUrl}
                  alt={org?.name ?? "Praticien"}
                  className="h-8 w-8"
                  fallback={
                    <div className="bg-primary text-primary-foreground flex h-full w-full items-center justify-center rounded-full text-xs font-bold">
                      {org?.name?.[0] ?? "C"}
                    </div>
                  }
                />
              ) : (
                <div className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold">
                  C
                </div>
              )}
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-bold">
                  {isAllProsMode ? "Cliently" : (org?.name ?? "Cliently")}
                </p>
                {!isAllProsMode && org?.professionType && (
                  <p className="text-muted-foreground truncate text-[10px]">
                    {org.professionType === "GENERIC"
                      ? org.customProfession || t("portal.practitioner")
                      : (() => {
                          const key = portalProfessionTranslationKey(
                            org.professionType,
                          );
                          return key ? t(key) : org.professionType;
                        })()}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="mx-auto">
              {!isAllProsMode && coachImageUrl ? (
                <CircleImageWithSkeleton
                  src={coachImageUrl}
                  alt={org?.name ?? "Praticien"}
                  className="h-8 w-8"
                  fallback={
                    <div className="bg-primary text-primary-foreground flex h-full w-full items-center justify-center rounded-full text-xs font-bold">
                      {org?.name?.[0] ?? "C"}
                    </div>
                  }
                />
              ) : (
                <div className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold">
                  C
                </div>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="hidden shrink-0 md:flex"
          >
            {sidebarOpen ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </div>

        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => {
                if (window.innerWidth < 768) setSidebarOpen(false);
              }}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-sm font-medium transition-[color,background-color,border-color]",
                  isActive
                    ? "bg-primary/[0.07] text-primary border-primary/25"
                    : "text-sidebar-foreground/70 hover:border-border/70 hover:bg-muted/45 hover:text-sidebar-foreground",
                  !sidebarOpen && "justify-center px-2",
                )
              }
            >
              <span className="relative shrink-0">
                <item.icon className="h-5 w-5" />
                {item.to === "/chat" && unreadChatCount > 0 ? (
                  <span className="bg-destructive text-destructive-foreground absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                    {unreadChatCount > 9 ? "9+" : unreadChatCount}
                  </span>
                ) : null}
              </span>
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="flex justify-center p-2">
          <NavLink
            to={helpItem.to}
            onClick={() => {
              if (window.innerWidth < 768) setSidebarOpen(false);
            }}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-xs font-normal transition-[color,background-color,border-color]",
                isActive
                  ? "bg-primary/[0.07] text-primary border-primary/25"
                  : "text-sidebar-foreground/60 hover:border-border/70 hover:bg-muted/45 hover:text-sidebar-foreground",
              )
            }
          >
            <helpItem.icon
              className="h-4 w-4 shrink-0 opacity-90"
              aria-hidden
            />
            {sidebarOpen && <span>{helpItem.label}</span>}
          </NavLink>
        </div>

        <div className="border-t p-2">
          <div className="flex items-center justify-center px-3 py-2">
            <p className="text-muted-foreground text-xs">
              {sidebarOpen ? "Cliently" : ""}
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};
